import React from 'react';
import * as Sentry from '@sentry/react';

const DOM_ERROR_PATTERNS = ['insertBefore', 'removeChild', 'The node before which the new node is to be inserted', 'The node to be removed is not a child of this node'];

function isDomManipulationError(error: Error): boolean {
  const msg = error.message || '';
  const isKnownNotFound = error.name === 'NotFoundError' && (msg.includes('insertBefore') || msg.includes('removeChild'));
  return isKnownNotFound || DOM_ERROR_PATTERNS.some((pattern) => msg.includes(pattern));
}

const MAX_RETRIES = 2;

interface Props {
  children: React.ReactNode;
}

interface State {
  retryCount: number;
  retryKey: number;
  lastError: Error | null;
}

/**
 * Catches DOM manipulation errors caused by browser extensions (Grammarly,
 * Google Translate, ad-blockers, ...) that modify the DOM behind React's back.
 * Instead of showing the full ErrorPage, it silently remounts the subtree
 * up to MAX_RETRIES times. Non-DOM errors are re-thrown so the router's
 * errorElement can handle them as before.
 */
export class RetryErrorBoundary extends React.Component<Props, State> {
  state: State = { retryCount: 0, retryKey: 0, lastError: null };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const isDomError = isDomManipulationError(error);
    if (!isDomError) {
      this.setState({ lastError: error });
      return;
    }

    if (this.state.retryCount >= MAX_RETRIES) {
      Sentry.captureException(error, {
        tags: { errorSource: 'RetryErrorBoundary', errorType: 'dom-manipulation-exhausted', autoRecovered: 'false' },
        contexts: { retryInfo: { retryCount: this.state.retryCount, componentStack: info.componentStack } },
      });
      this.setState({ lastError: error });
      return;
    }

    const nextRetryCount = this.state.retryCount + 1;

    Sentry.addBreadcrumb({
      category: 'error-boundary',
      message: `DOM manipulation error auto-recovered (attempt ${nextRetryCount}/${MAX_RETRIES})`,
      level: 'warning',
      data: { error: error.message, retryAttempt: nextRetryCount },
    });

    this.setState((prev) => ({
      retryCount: nextRetryCount,
      retryKey: prev.retryKey + 1,
    }));
  }

  render() {
    if (this.state.lastError) {
      throw this.state.lastError;
    }
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
